import { v4 as uuidv4 } from 'uuid';
import { DefaultAllowedMimeTypeUpload } from '../constants';
import { Convert } from './ConvertMimeTypes';
export default class Utils {
  static camelToKebabCase(text: string) {
    return text.replace(/[A-Z]/g, '-$&').toLowerCase();
  }

  static camelToSnakeCase(text: string) {
    return text.replace(/[A-Z]/g, '_$&').toLowerCase();
  }

  static capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  static formatCnpj(cnpj: string): string {
    if (!cnpj) return '';

    const cleaned = cnpj.replace(/\D/g, '');

    if (cleaned.length !== 14) return cnpj;

    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  static parseKeyText(text: string, params = {}) {
    const hasObjKey = /({.*})/.test(text);
    const arrayParams = Object.keys(params);

    if (hasObjKey && arrayParams.length) {
      for (const paramKey of arrayParams) {
        if (Array.isArray(params[paramKey])) {
          text += params[paramKey].map((item) => `&${paramKey}=${item}`).join('');
        } else {
          text = text.replace(`{${paramKey}}`, params[paramKey]);
        }
      }
    }

    return text;
  }

  static removeEmptyFromObject(obj = {}): any {
    Object.keys(obj).forEach(
      (k) =>
        (obj[k] === null || obj[k] === undefined || Object.keys(obj).length === 0) && delete obj[k]
    );
    return obj;
  }

  static isAlphaNumeric(char: string): boolean {
    return /^[\w]*$/.test(char);
  }

  static isName(char: string): boolean {
    return /^[a-zA-Z\s\d\wÀ-ú]*$/.test(char);
  }

  static notNull(value: any): boolean {
    return value !== null && value !== undefined;
  }

  static hasNumber(str: string): boolean {
    return /\d/.test(str);
  }

  static hasSpecialChar(str: string): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(str);
  }

  static hasUpperCaseAndLowerCase(str: string): boolean {
    return /[a-z]/.test(str) && /[A-Z]/.test(str);
  }

  static sortByKey(key: string, array: any[] = []) {
    return array.sort((after, before) => before[key].localeCompare(after[key]));
  }

  static formatDate(
    rawDate: Date | string,
    format:
      | 'MM-DD-YYYY'
      | 'DD-MM-YYYY'
      | 'DD_MM_YYYY'
      | 'YYYY-MM-DD'
      | 'DD/MM/YYYY'
      | 'DD/MM/YYYY HH:MM'
  ): string {
    try {
      let date = rawDate;

      if (typeof date === 'string') {
        date = new Date(date);
      }

      let day: string | number = date.getDate();
      let month: string | number = date.getMonth() + 1;
      const year = date.getFullYear();

      if (year < 1900) {
        return '';
      }

      day = day < 10 ? `0${day}` : day;
      month = month < 10 ? `0${month}` : month;

      if (format === 'MM-DD-YYYY') {
        return `${month}-${day}-${year}`;
      }

      if (format === 'DD-MM-YYYY') {
        return `${day}-${month}-${year}`;
      }

      if (format === 'DD_MM_YYYY') {
        return `${day}_${month}_${year}`;
      }

      if (format === 'DD/MM/YYYY') {
        return `${day}/${month}/${year}`;
      }

      if (format === 'YYYY-MM-DD') {
        return `${year}-${month}-${day}`;
      }

      if (format === 'DD/MM/YYYY HH:MM') {
        let hour: string | number = date.getHours();
        let minutes: string | number = date.getMinutes();

        hour = hour < 10 ? `0${hour}` : hour;
        minutes = minutes < 10 ? `0${minutes}` : minutes;

        return `${day}/${month}/${year} ${hour}:${minutes}`;
      }

      throw new Error('Formato novo de data não implementado');
    } catch (err) {
      console.error('err', err);
    }
  }

  static createAndDownloadBlobFile(body, filename, extension = 'pdf') {
    const blob = new Blob([body]);
    const fileName = `${filename}.${extension}`;
    if ((navigator as any).msSaveBlob) {
      // IE 10+
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement('a');
      // Browsers that support HTML5 download attribute
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  static blobToBase64Async(blob: Blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  static isValidCpf(cpf: string): boolean {
    //font: http://www.receita.fazenda.gov.br/aplicacoes/atcta/cpf/funcoes.js
    const commonFalses = [
      '00000000000',
      '11111111111',
      '22222222222',
      '33333333333',
      '44444444444',
      '55555555555',
      '66666666666',
      '77777777777',
      '88888888888',
      '99999999999',
      '12345678901',
    ];
    const cpfCleaned = cpf.replace('.', '').replace('-', '').trim();
    if (commonFalses.some((item) => item === cpfCleaned)) {
      return false;
    }
    let soma = 0;
    let resto = 0;
    for (let index = 1; index <= 9; index++) {
      soma = soma + parseInt(cpfCleaned.substring(index - 1, index), 10) * (11 - index);
    }
    resto = (soma * 10) % 11;

    if (resto === 10 || resto === 11) {
      resto = 0;
    }

    if (resto !== parseInt(cpfCleaned.substring(9, 10), 10)) {
      return false;
    }

    soma = 0;

    for (let index = 1; index <= 10; index++) {
      soma = soma + parseInt(cpfCleaned.substring(index - 1, index), 10) * (12 - index);
    }

    resto = (soma * 10) % 11;

    if (resto === 10 || resto === 11) {
      resto = 0;
    }

    if (resto !== parseInt(cpfCleaned.substring(10, 11), 10)) {
      return false;
    }

    return true;
  }
  static newGuid() {
    return uuidv4();
  }

  static getRandomCharacters(options?: { size?: number; except?: string }): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomString = '';
    const size = options?.size || 5;
    for (let i = 0; i < size; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      randomString += characters.charAt(randomIndex);
    }

    if (options && randomString === options.except) {
      return this.getRandomCharacters(options);
    }

    return randomString;
  }

  static randomFilenameGenerator(prefix?: string, extension?: string): string {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    };
    const filename = new Intl.DateTimeFormat('pt-BR', dateOptions).format(new Date());
    if (prefix) {
      prefix = `${prefix}_`;
    }
    if (extension) {
      extension = `.${extension}`;
    }
    return `${prefix}${this.getRandomCharacters()}_${filename
      .replace(',', '')
      .replace(/(\/|\:|\s)/g, '-')}${extension}`;
  }

  static getFileTypeFrom(filename: string) {
    const extension = filename.toLowerCase().split('.').pop();
    const mimeType = Object.entries(Convert.mimeTypes).reduce((acc, [type, extensionList]) => {
      if (!acc && extensionList.includes(extension)) {
        return type;
      }
      return acc;
    }, null);
    if (mimeType === null) {
      return '';
    }
    return mimeType as string;
  }

  static isMimeTypeAllowed(mimeType: string) {
    return DefaultAllowedMimeTypeUpload.some((allowedMimeType: string | any) => {
      if (allowedMimeType === mimeType) {
        return true;
      }

      if (allowedMimeType?.endsWith('/*') && mimeType.startsWith(allowedMimeType.slice(0, -2))) {
        return true;
      }

      return false;
    });
  }

  static convertSrcToFile(src: string, fileName: string): Promise<File | null> {
    return new Promise((resolve, reject) => {
      if (src.startsWith('data:')) {
        const arr = src.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
          u8arr[i] = bstr.charCodeAt(i);
        }
        resolve(new File([u8arr], fileName, { type: mime }));
      } else {
        fetch(src)
          .then((response) => response.blob())
          .then((blob) => {
            resolve(new File([blob], fileName, { type: blob.type }));
          })
          .catch((error) => {
            reject(`Erro ao converter URL para File: ${error}`);
          });
      }
    });
  }

  static formatPhone(phone: string): string {
    if (!phone) return '';

    let cleaned = phone.replace(/\D/g, '');

    if (cleaned.startsWith('55') && cleaned.length > 11) {
      cleaned = cleaned.substring(2);
    }

    if (cleaned.length === 11) {
      return cleaned.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }

    if (cleaned.length === 10) {
      return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }

    return phone;
  }

  static formatIE(ie: string): string {
    if (!ie) return '';

    const cleaned = ie.replace(/\D/g, '');

    return cleaned.replace(/(\d{3})(?=\d)/g, '$1.').replace(/\.$/, '');
  }

  static arrayBufferToJson(arrayBuffer: ArrayBuffer): any {
    const textDecoder = new TextDecoder('utf-8');
    const jsonString = textDecoder.decode(arrayBuffer);
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return null;
    }
  }
}
